import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { MaterialReactTable } from "material-react-table";
import {
  Box,
  Button,
  FormControlLabel,
  Switch,
  Typography,
} from "@mui/material";
import axiosInstance from "../api/axiosConfig";
import { formatDisplayDateTime } from "../utils/formatters";
import { isDevSite, useDevSiteOverrides } from "../utils/devSites";
import { isExpired, isPaidPlan } from "../utils/subscription";
import { devSiteColumn, devSiteRowProps } from "../components/DevSiteSwitch";
import { expiredRowClass, planStatusColumn } from "../components/PlanStatus";
import "./PaidUsers.css";

const PaidUsers = () => {
  const [installations, setInstallations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDevSites, setShowDevSites] = useState(false);
  const [showExpired, setShowExpired] = useState(false);
  const navigate = useNavigate();
  const devSiteOverrides = useDevSiteOverrides();

  useEffect(() => {
    if (localStorage.getItem("isAuthenticated") !== "true") {
      navigate("/admin-login");
      return;
    }

    const fetchPaidUsers = async () => {
      try {
        const response = await axiosInstance.get("/admin/installations");
        setInstallations(response.data.installations || []);
      } catch (error) {
        console.error("Error loading paid users:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchPaidUsers();
  }, [navigate]);

  const allRows = useMemo(
    () =>
      installations
        // `isPaidPlan` and not just "has a plan": a plan recorded as "free" is
        // the absence of a purchase, so it must never reach the paid list.
        .filter((item) => isPaidPlan(item.plan) && item.app_installed === 1)
        .map((item, index) => ({
          id: item.id ?? index,
          instanceId: item.instanceId || "—",
          installed_at: item.installed_at ?? null,
          reinstalled_at: item.reinstalled_at ?? null,
          uninstalled_at: item.uninstalled_at ?? null,
          app_installed: item.app_installed === 1 ? "Yes" : "No",
          plan: item.plan ?? "—",
          expires_on: item.expires_on ?? null,
          cycle: item.cycle ?? "—",
          url: item.url ?? "—",
          auto_renew:
            item.auto_renew === 1 ? "Yes" : item.auto_renew === 0 ? "No" : "—",
          cancelReason: item.cancelReason ?? "—",
          userReason: item.userReason ?? "—",
          subscriptionCancellationType:
            item.subscriptionCancellationType ?? "—",
          site_name: item.site_name ?? "—",
          plan_purchase_at: item.plan_purchase_at ?? null, // use raw date only
          is_dev_site: isDevSite(item, devSiteOverrides),
          is_expired: isExpired(item),
          raw: item,
        })),
    [installations, devSiteOverrides],
  );

  // Neither development sites nor lapsed plans are real paid users: they never
  // count, and they stay hidden unless the admin asks to see them (to un-mark a
  // dev site, or to check when a plan ran out, for example).
  const countedRows = useMemo(
    () => allRows.filter((row) => !row.is_dev_site && !row.is_expired),
    [allRows],
  );
  const devRowCount = allRows.filter((row) => row.is_dev_site).length;
  const expiredRowCount = allRows.filter(
    (row) => !row.is_dev_site && row.is_expired,
  ).length;
  const rows = allRows.filter(
    (row) =>
      (showDevSites || !row.is_dev_site) && (showExpired || !row.is_expired),
  );

  const handleRowClick = (row) => {
    navigate(`/details/${row.original.id}`, { state: row.original.raw });
  };

  const columns = [
    {
      accessorFn: (row, index) => index + 1,
      id: "serial",
      header: "ID",
      size: 70,
      Cell: ({ row }) => row.index + 1,
    },
    {
      accessorKey: "instanceId",
      header: "Instance ID",
      size: 140,
      muiTableBodyCellProps: {
        className: "mrt-cell-instanceId",
      },
    },
    {
      accessorFn: (row) =>
        row.installed_at ? new Date(row.installed_at) : null,
      id: "installed_at",
      header: "Installed At",
      sortingFn: "datetime",
      Cell: ({ cell }) => formatDisplayDateTime(cell.getValue()),
      size: 175,
    },
    {
      accessorFn: (row) =>
        row.reinstalled_at ? new Date(row.reinstalled_at) : null,
      id: "reinstalled_at",
      header: "Reinstalled At",
      sortingFn: "datetime",
      Cell: ({ cell }) => formatDisplayDateTime(cell.getValue()),
      size: 172,
    },
    {
      accessorFn: (row) =>
        row.uninstalled_at ? new Date(row.uninstalled_at) : null,
      id: "uninstalled_at",
      header: "Uninstalled At",
      sortingFn: "datetime",
      Cell: ({ cell }) => formatDisplayDateTime(cell.getValue()),
      size: 170,
    },
    { accessorKey: "app_installed", header: "App Installed", size: 110 },
    devSiteColumn,
    { accessorKey: "plan", header: "Plan", size: 100 },
    planStatusColumn,
    {
      accessorFn: (row) => (row.expires_on ? new Date(row.expires_on) : null),
      id: "expires_on",
      header: "Expires On",
      sortingFn: "datetime",
      Cell: ({ cell }) => formatDisplayDateTime(cell.getValue()),
      size: 172,
    },
    { accessorKey: "cycle", header: "Cycle", size: 100 },
    {
      accessorKey: "url",
      header: "URL",
      size: 150,
      muiTableBodyCellProps: {
        className: "mrt-cell-url",
      },
      Cell: ({ cell }) =>
        cell.getValue() !== "—" ? (
          <a
            href={cell.getValue()}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            className="paid-users-link"
          >
            {cell.getValue()}
          </a>
        ) : (
          <span>—</span>
        ),
    },
    { accessorKey: "auto_renew", header: "Auto Renew", size: 100 },
    { accessorKey: "cancelReason", header: "Cancel Reason", size: 150 },
    { accessorKey: "userReason", header: "User Reason", size: 150 },
    {
      accessorKey: "subscriptionCancellationType",
      header: "Cancellation Type",
      size: 160,
    },
    { accessorKey: "site_name", header: "Site Name", size: 150 },
    {
      header: "Plan Purchased At",
      accessorFn: (row) =>
        row.plan_purchase_at ? new Date(row.plan_purchase_at) : null,
      id: "plan_purchase_at",
      sortingFn: "datetime",
      Cell: ({ cell }) => formatDisplayDateTime(cell.getValue()),
      size: 180,
    },
  ];

  return (
    <Box className="paid-users-container">
      <Box className="paid-users-header">
        <Typography variant="h5" className="paid-users-title">
          Paid Users ({countedRows.length})
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          {devRowCount > 0 && (
            <FormControlLabel
              className="dev-site-filter"
              control={
                <Switch
                  size="small"
                  checked={showDevSites}
                  onChange={(e) => setShowDevSites(e.target.checked)}
                />
              }
              label={`Show dev sites (${devRowCount})`}
            />
          )}
          {expiredRowCount > 0 && (
            <FormControlLabel
              className="dev-site-filter"
              control={
                <Switch
                  size="small"
                  checked={showExpired}
                  onChange={(e) => setShowExpired(e.target.checked)}
                />
              }
              label={`Show expired (${expiredRowCount})`}
            />
          )}
          <Button
            variant="contained"
            className="paid-users-go-back-btn"
            onClick={() => navigate(-1)}
          >
            ⬅ Go Back
          </Button>
        </Box>
      </Box>

      <Box className="paid-users-table-wrapper">
        <MaterialReactTable
          columns={columns}
          data={rows}
          state={{ isLoading: loading }}
          enableColumnOrdering
          enablePagination
          enableSorting
          enableBottomToolbar
          enableColumnResizing
          columnResizeMode="onChange"
          muiTableContainerProps={{ sx: { height: "100%" } }}
          initialState={{
            pagination: { pageSize: 20 },
            sorting: [
              {
                id: "plan_purchase_at",
                desc: true,
              },
            ],
          }}
          muiTableBodyRowProps={({ row }) => ({
            className: [devSiteRowProps(row).className, expiredRowClass(row)]
              .filter(Boolean)
              .join(" "),
            onClick: () => handleRowClick(row),
            sx: { cursor: "pointer" },
          })}
        />
      </Box>
    </Box>
  );
};

export default PaidUsers;
